import { ElementType, useCallback, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import ChevronRightTwoTone from '@mui/icons-material/ChevronRightTwoTone';
import ClearIcon from '@mui/icons-material/ClearTwoTone';
import classNames from 'classnames/bind';
import { getObjectChanges } from '@/Infrastructure/Utilities/object.type';
import DynamicSelectContainer from '@components/DynamicSelectContainer';
import { CustomFieldProps } from '../types';
import FormLabel from '../FormLabel';

import styles from './ButtonDropdown.module.css';
const classCtx = classNames.bind(styles);

interface ButtonDropdownProps {
  /** Content to display on the button such as the currently selected item */
  ButtonContent: ElementType;
  /** */
  buttonContentProps?: unknown;
  buttonConfig?: {
    height?: string | number;
    width?: string | number;
    className?: string;
    hideArrow?: boolean;
  };
  label?: string;
  /** Item to display when the button is clicked and the popup opens */
  PopupContent: ElementType;
  /** */
  popupContentProps?: { className?: string } & Record<string, unknown>;
  /** Popup configurations */
  popupConfig?: {
    height?: string | number;
    width?: string | number;
  };
  /** Callback for updating the Formik field */
  fieldOnChange?: (value: unknown) => void;
  isError?: boolean;
  error?: string;
  /** Flag indicating the input has changed from its initial value */
  isDirty?: boolean;
  /** Flag indicating the input is currently in a disabled state */
  isDisabled?: boolean;
  /** Function to execute when the clear button is clicked */
  onClear?: () => void;
  /** Class to be applied to the outermost element */
  className?: string;
  required?: boolean;
}

export interface PopupContentProps {
  /** On Change callback to execute when an item is selected */
  onChange: (value: unknown) => void;
  /** Dispatch event to close the menu */
  closeMenu: () => void;
}

/**
 *  Button Dropdown component that renders a MUI Button with the selected value displayed. Clicking the button will open
 *  a Popup when the screen size permits, or a Drawer in smaller resolutions. The popup should contain items that can be selected
 *  to replace the button content.
 */
export default function ButtonDropdown({ ButtonContent, PopupContent, ...props }: ButtonDropdownProps) {
  /** Html Element for the popover to anchor to */
  const [anchorEl, setAnchorEl] = useState<HTMLElement | SVGElement | null>(null);

  function onItemSelect(value: unknown) {
    if (props.fieldOnChange) {
      props.fieldOnChange(value);
    }
    setAnchorEl(null);
  }

  const containerStyles = classCtx({
    container: true,
    [props.className ?? '']: Boolean(props.className)
  });

  const buttonStyles = classCtx({
    'button-dropdown': true,
    dirty: props.isDirty,
    disabled: props.isDisabled,
    error: props.isError,
    [props.buttonConfig?.className ?? '']: Boolean(props.buttonConfig?.className)
  });

  return (
    <div
      className={containerStyles}
      style={{
        width: props.buttonConfig?.width,
        height: props.buttonConfig?.height
      }}>
      {(props.label || props.error) && (
        <FormLabel
          text={props.label}
          error={!props.label || (props.isError && !props.isDisabled)}
          errorText={props.error}
          className={props.label ? styles.label : styles['button-error-ico']}
          required={props.required}
        />
      )}
      <Button
        variant='contained'
        color='primary'
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
        }}
        className={buttonStyles}
        disableRipple
        disabled={props.isDisabled}>
        <>
          <div className={styles['button-content-component']}>
            <ButtonContent {...(props.buttonContentProps ?? {})} />
          </div>

          {props.onClear && (
            <ClearIcon
              className={styles.clear}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                // This wouldn't render if this was undefined but apparently linter can't tell
                if (props.onClear) {
                  props.onClear();
                }
              }}
            />
          )}

          {!props.buttonConfig?.hideArrow && (
            <div className={styles['arrow']}>
              <ChevronRightTwoTone className={styles.rotated} />
            </div>
          )}
        </>
      </Button>

      <DynamicSelectContainer
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        height={props.popupConfig?.height}
        width={props.popupConfig?.width}
        className={props.popupContentProps?.className}>
        <PopupContent
          onChange={onItemSelect}
          closeMenu={() => {
            setAnchorEl(null);
          }}
          {...(props.popupContentProps ?? {})}
        />
      </DynamicSelectContainer>
    </div>
  );
}

function asButtonDropdown({
  buttonConfig,
  ButtonContent,
  buttonContentProps,
  PopupContent,
  popupContentProps
}: Pick<ButtonDropdownProps, 'buttonConfig' | 'ButtonContent' | 'buttonContentProps' | 'PopupContent' | 'popupContentProps'>) {
  return ({ field: { name }, form, onChangeExtension, ...props }: CustomFieldProps) => {
    const { value, initialValue, error, touched } = form.getFieldMeta(name);
    const { dirty, setFieldValue, setFieldTouched } = form;

    const isDirty = useMemo(() => {
      if (!dirty) {
        return false;
      }

      if (value && typeof value === 'object') {
        if (!initialValue) return true;
        const valRecord = value as Record<string, unknown>;
        const initRecord = initialValue as Record<string, unknown>;
        const differences = getObjectChanges(valRecord, initRecord);
        return Object.keys(differences).length > 0;
      }

      return value !== initialValue;
    }, [dirty, initialValue, value]);

    const onFieldValueChangeHandler = useCallback(
      (value?: unknown) => {
        void setFieldTouched(name, true);
        void setFieldValue(name, value || null, true);

        if (onChangeExtension) {
          onChangeExtension(value);
        }
      },
      [name, onChangeExtension, setFieldValue, setFieldTouched]
    );

    return (
      <ButtonDropdown
        className={props.className}
        ButtonContent={ButtonContent}
        buttonContentProps={buttonContentProps}
        PopupContent={PopupContent}
        popupContentProps={popupContentProps}
        fieldOnChange={onFieldValueChangeHandler}
        buttonConfig={{
          ...buttonConfig
        }}
        popupConfig={{
          width: props.width,
          height: props.height
        }}
        label={props.label}
        isError={touched && Boolean(error)}
        error={error}
        isDirty={isDirty}
        isDisabled={form.isSubmitting || props.disabled}
        onClear={props.clearable ? onFieldValueChangeHandler : undefined}
        required={props.required}
      />
    );
  };
}

export const FormikButtonDropdown = (
  props: Pick<ButtonDropdownProps, 'buttonConfig' | 'ButtonContent' | 'buttonContentProps' | 'PopupContent' | 'popupContentProps'>
) => asButtonDropdown({ ...props });
